import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import { useParams } from "@tanstack/react-router";

export enum CareplanAvailableAction {
  READ_RECOMMENDATION = "read_recommendation",
  READ_DISPOSITION = "read_disposition",
  READ_FOLLOW_UP = "read_follow_up",
  READ_LAB = "read_lab",
  READ_PRESCRIPTION = "read_prescription",
  READ_REFERRAL = "read_referral",

  ADD_RECOMMENDATION = "add_recommendation",
  ADD_DISPOSITION = "add_disposition",
  ADD_FOLLOW_UP = "add_follow_up",
  ADD_LAB = "add_labs",
  ADD_PRESCRIPTION = "add_prescription",
  ADD_REFERRAL = "add_referral",

  UPDATE_FOLLOW_UP = "update_follow_up",

  SEND_CARE_PLAN = "send_care_plan",
  VOID_CARE_PLAN = "void_care_plan",
}

export enum CareplanAvailableActionsUnavailabilityReason {
  INVALID_ENCOUNTER_ASSIGNMENT = "INVALID_ENCOUNTER_ASSIGNMENT",
  INVALID_ENCOUNTER_STATUS = "INVALID_ENCOUNTER_STATUS",
  UNAUTHORIZED_ROLE = "UNAUTHORIZED_ROLE",
  HAS_OPEN_LAB_ORDERS = "HAS_OPEN_LAB_ORDERS",
  HAS_RX_ORDERS = "HAS_RX_ORDERS",
  HAS_REFERRAL = "HAS_REFERRAL",
  HAS_SENT_RX_ORDERS = "HAS_SENT_RX_ORDERS",
  ALREADY_HAS_FOLLOW_UP = "ALREADY_HAS_FOLLOW_UP",
  IN_VOIDED_STATUS = "IN_VOIDED_STATUS",
  MISSING_DATA = "MISSING_DATA",
  NO_EXISTING_FOLLOW_UP = "NO_EXISTING_FOLLOW_UP",
}

export interface CareplanAvailableActionsResponse_Action {
  action: CareplanAvailableAction;
  unavailableReasons: CareplanAvailableActionsUnavailabilityReason[];
}

export interface CarePlanSummaryResponse {
  summary?: {
    careplanId: string;
    childEncounterId: string;
    motherEncounterId: string;
    dispositionSummaryCount: number;
    hasFollowUp: boolean;
    labSummaryCount: number;
    recommendationSummaryCount: number;
    rxSummaryCount: number;
    hasAmazonReferOutActivity: boolean;
  };
  availableActions: CareplanAvailableActionsResponse_Action[];
}

interface UseCarePlanSummaryOptions {
  enabled?: boolean;
}

export const useCarePlanSummary = (
  encounterId: string,
  options?: UseCarePlanSummaryOptions,
) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  return useApi<CarePlanSummaryResponse>({
    queryOptions: {
      enabled: Boolean(encounterId) && (options?.enabled ?? true),
      queryFn: async () => {
        const response = await axiosInstance.get<CarePlanSummaryResponse>(
          `/charting/encounter-charting/${encounterId}/careplan/summary`,
        );

        return response.data;
      },
      queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.SUMMARY({
        patientId,
        encounterId,
      }),
    },
  });
};
